import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { getStudentTestDetail, submitStudentTest, abandonStudentTest } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, Loader2, Star, AlertCircle, Clock, Layers, X } from "lucide-react";
import { toast } from "sonner";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function StudentTestTake() {
  const { testId } = useParams();
  const { learner } = useRole();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [timeExpired, setTimeExpired] = useState(false);

  // Abandon & exit warning state
  const [abandoned, setAbandoned] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);

  // Timer state
  const [timeLeft, setTimeLeft] = useState(null);   // seconds remaining
  const timerRef = useRef(null);
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    if (!learner?.learner_id || !testId) return;
    getStudentTestDetail(learner.learner_id, testId)
      .then((data) => {
        setTest(data);
        if (data.status === "submitted" || data.status === "completed") {
          setSubmitted(true);
        }
        if (data.status === "abandoned") {
          setSubmitted(true);
          setAbandoned(true);
        }
        if (data.my_answers) {
          const prefilled = {};
          data.my_answers.forEach((a) => {
            prefilled[a.question_number] = {
              answer_type: a.answer_type,
              selected_option: a.selected_option,
              typed_answer: a.typed_answer,
              is_correct: a.is_correct,
              marks_awarded: a.marks_awarded,
            };
          });
          setAnswers(prefilled);
        }
        // Start countdown for FM tests and timed practice tests
        const timerMins = data.fm_timer_minutes || data.timer_minutes;
        if (timerMins && !(data.status === "submitted" || data.status === "completed" || data.status === "abandoned")) {
          setTimeLeft(timerMins * 60);
        }
      })
      .catch(() => toast.error("Could not load test"))
      .finally(() => setLoading(false));
  }, [learner?.learner_id, testId]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft === null || submitted) return;
    if (timeLeft <= 0) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeLeft === null ? null : "started", submitted]); // only run once when timer is set

  // Auto-redirect after Time's Up
  useEffect(() => {
    if (!timeExpired) return;
    const timer = setTimeout(() => navigate("/home"), 3000);
    return () => clearTimeout(timer);
  }, [timeExpired, navigate]);

  // Auto-submit when time hits zero
  useEffect(() => {
    if (timeLeft === 0 && !hasAutoSubmitted.current && !submitted && test) {
      hasAutoSubmitted.current = true;
      handleAutoSubmit();
    }
  }, [timeLeft]);

  // Block browser back button during active test
  useEffect(() => {
    if (submitted || loading || !test) return;
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setShowExitWarning(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [submitted, loading, test]);

  const handleSelect = (questionNumber, option) => {
    setAnswers((prev) => ({
      ...prev,
      [questionNumber]: { ...prev[questionNumber], selected_option: option, answer_type: "MCQ" },
    }));
  };

  const handleTyped = (questionNumber, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionNumber]: { ...prev[questionNumber], typed_answer: value, answer_type: "TITA" },
    }));
  };

  const buildPayload = useCallback(
    (testData, currentAnswers) =>
      (testData?.questions || []).map((q) => {
        const ans = currentAnswers[q.question_number] || {};
        return {
          question_number: q.question_number,
          answer_type: q.answer_type,
          selected_option: ans.selected_option || null,
          typed_answer: ans.typed_answer || null,
        };
      }),
    []
  );

  const handleAutoSubmit = async () => {
    if (!test) return;
    clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const payload = buildPayload(test, answers);
      const result = await submitStudentTest(testId, learner.learner_id, payload);
      setSubmitResult(result);
      setSubmitted(true);
      setTimeExpired(true);
    } catch {
      // Silent — show time's up screen regardless
      setSubmitted(true);
      setTimeExpired(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!test) return;
    const unanswered = test.questions.filter((q) => {
      const ans = answers[q.question_number];
      if (q.answer_type === "MCQ") return !ans?.selected_option;
      return false;
    });
    if (unanswered.length > 0) {
      toast.error(`Please answer all MCQ questions (${unanswered.length} remaining)`);
      return;
    }

    clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const payload = buildPayload(test, answers);
      const result = await submitStudentTest(testId, learner.learner_id, payload);
      setSubmitResult(result);
      setSubmitted(true);
      toast.success("Test submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAbandon = async () => {
    if (!test) return;
    clearInterval(timerRef.current);
    setAbandoning(true);
    try {
      const payload = buildPayload(test, answers);
      await abandonStudentTest(testId, learner.learner_id, payload);
      setAbandoned(true);
      setSubmitted(true);
      setShowExitWarning(false);
      setShowAbandonConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to abandon test");
    } finally {
      setAbandoning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-slate-400" data-testid="test-take-loading">
        <Loader2 className="w-8 h-8 animate-spin mr-2" /> Loading test...
      </div>
    );
  }

  if (!test) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="test-not-found">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-slate-600">Test not found or not assigned to you.</p>
        <Button variant="outline" onClick={() => navigate("/tests")} className="mt-4">
          Back to My Tests
        </Button>
      </div>
    );
  }

  const isFM = test.test_type === "FM";

  // Abandoned screen
  if (submitted && abandoned) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="test-abandoned-screen">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <X className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-2">Test Abandoned</h2>
        <p className="text-slate-500 mb-6">
          Your partial answers have been recorded. Your teacher can see your progress.
        </p>
        <Button
          onClick={() => navigate("/tests")}
          variant="outline"
          data-testid="abandoned-back-btn"
        >
          Back to My Tests
        </Button>
      </div>
    );
  }

  // Time's Up screen (timer expired)
  if (timeExpired) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="times-up-screen">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-2">Time&apos;s Up!</h2>
        <p className="text-slate-500 mb-2 font-medium">Test Submitted</p>
        <p className="text-slate-400 text-sm mb-8">Your answers have been recorded. Redirecting you home...</p>
        <Button
          onClick={() => navigate("/home")}
          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          data-testid="times-up-home-btn"
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  // Submitted — not FM (teacher-graded TITA)
  if (submitted && !isFM && !test.scores_released) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="test-submitted-screen">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-2">Test Submitted!</h2>
        <p className="text-slate-500 mb-6">
          Great job completing the test. Your teacher will review your answers and share the results soon.
        </p>
        <Button
          onClick={() => navigate("/tests")}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="back-to-tests-btn"
        >
          Back to My Tests
        </Button>
      </div>
    );
  }

  // FM submitted — auto-graded results available
  if (submitted && isFM) {
    const totalScore = submitResult?.total_score ?? test.score;
    const totalMarks = test.total_marks;
    const pct = totalMarks ? Math.round(((totalScore || 0) / totalMarks) * 100) : null;
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="fm-test-results-screen">
        <button
          onClick={() => navigate("/tests")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Tests
        </button>

        <Card className="rounded-2xl p-6 mb-6 bg-gradient-to-br from-fuchsia-50 to-sky-50 border border-fuchsia-200">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-fuchsia-100 rounded-full flex items-center justify-center">
              <Layers className="w-7 h-7 text-fuchsia-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-slate-600 text-sm">FM Test Score</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700">FM</span>
              </div>
              <p className="font-display text-3xl font-bold text-fuchsia-700" data-testid="fm-student-score">
                {totalScore !== null && totalScore !== undefined ? totalScore : "—"}
                {totalMarks ? <span className="text-xl text-slate-500"> / {totalMarks}</span> : null}
              </p>
              {pct !== null && (
                <p className="text-sm text-slate-500 mt-0.5">{pct}% correct</p>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-3">
          {test.questions.map((q, idx) => {
            const myAns = answers[q.question_number] || {};
            const isCorrect = myAns.is_correct;
            return (
              <Card
                key={idx}
                className={`rounded-xl p-4 border ${isCorrect ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}
              >
                <p className="font-semibold text-slate-800 text-sm mb-2">
                  Q{q.question_number}. {q.question_text}
                </p>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-slate-500">Your answer:</span>
                  <span className={`font-bold ${isCorrect ? "text-emerald-700" : "text-red-600"}`}>
                    {myAns.typed_answer || "—"}
                  </span>
                  {!isCorrect && q.correct_answer && (
                    <>
                      <span className="text-slate-400">·</span>
                      <span className="text-emerald-700 text-xs">Correct: {q.correct_answer}</span>
                    </>
                  )}
                  <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${isCorrect ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {isCorrect ? "+1" : "0"}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            onClick={() => navigate("/tests")}
            variant="outline"
            className="flex-1"
          >
            My Tests
          </Button>
          <Button
            onClick={() => navigate("/home")}
            className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="fm-results-home-btn"
          >
            Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Scores released for regular test
  if (submitted && test.scores_released) {
    const totalScore = test.score;
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="test-results-screen">
        <button
          onClick={() => navigate("/tests")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Tests
        </button>
        <Card className="rounded-2xl p-6 mb-6 bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
              <Star className="w-7 h-7 text-emerald-600 fill-emerald-400" />
            </div>
            <div>
              <p className="text-slate-600 text-sm">Your Score</p>
              <p className="font-display text-3xl font-bold text-emerald-700" data-testid="student-score">
                {totalScore !== null && totalScore !== undefined ? totalScore : "—"}
                {test.total_marks ? <span className="text-xl text-slate-500"> / {test.total_marks}</span> : null}
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          {test.questions.map((q, idx) => {
            const myAns = answers[q.question_number] || {};
            return (
              <Card key={idx} className="rounded-xl p-4 border border-slate-200">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-slate-800 text-sm">Q{q.question_number}. {q.question_text}</p>
                  {q.marks && <span className="text-xs text-slate-500 flex-shrink-0">[{q.marks} mark{q.marks !== 1 ? "s" : ""}]</span>}
                </div>
                {q.answer_type === "MCQ" ? (
                  <div className="text-sm">
                    <span className="text-slate-500">Your answer: </span>
                    <span className={myAns.is_correct ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>
                      {myAns.selected_option || "—"}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm">
                    <span className="text-slate-500">Your answer: </span>
                    <span className="text-slate-800">{myAns.typed_answer || "—"}</span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Active test — attempt screen
  const answeredCount = test.questions.filter((q) => {
    const ans = answers[q.question_number];
    if (q.answer_type === "MCQ") return !!ans?.selected_option;
    return !!ans?.typed_answer;
  }).length;

  // Timer color coding
  const timerUrgent = timeLeft !== null && timeLeft <= 60;
  const timerWarning = timeLeft !== null && timeLeft > 60 && timeLeft <= 180;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="test-take-screen">
      {/* Exit warning overlay */}
      {showExitWarning && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="exit-warning-overlay">
          <Card className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-display text-lg font-bold text-slate-900 text-center">Leave this test?</h3>
            <p className="text-sm text-slate-500 text-center">
              Your progress will be lost if you leave without submitting.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => setShowExitWarning(false)}
                className="bg-violet-600 hover:bg-violet-700 text-white w-full"
                data-testid="resume-test-btn"
              >
                Resume Test
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowExitWarning(false); setShowAbandonConfirm(true); }}
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                data-testid="abandon-from-warning-btn"
              >
                Abandon Test
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Abandon confirm dialog */}
      {showAbandonConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <Card className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4">
            <h3 className="font-display text-lg font-bold text-slate-900 text-center">Abandon this test?</h3>
            <p className="text-sm text-slate-500 text-center">
              Only the questions you answered will be scored. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAbandonConfirm(false)}
                className="flex-1"
                data-testid="cancel-abandon-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAbandon}
                disabled={abandoning}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-abandon-btn"
              >
                {abandoning ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Abandoning...</> : "Yes, Abandon"}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {/* Sticky FM countdown timer */}
      {timeLeft !== null && (
        <div
          className={`sticky top-0 z-30 mb-4 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 flex items-center justify-between text-sm font-semibold transition-colors ${
            timerUrgent
              ? "bg-red-600 text-white"
              : timerWarning
              ? "bg-amber-500 text-white"
              : "bg-fuchsia-600 text-white"
          }`}
          data-testid="fm-countdown-timer"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Time Remaining</span>
          </div>
          <span className="text-lg font-bold tabular-nums" data-testid="timer-display">
            {formatTime(timeLeft)}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setShowExitWarning(true)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition"
          data-testid="back-to-tests-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Tests
        </button>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="font-display text-2xl font-bold text-slate-800" data-testid="test-title">
            {test.title || `${test.subject} — ${test.chapter}`}
          </h1>
          {isFM && (
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 flex-shrink-0">
              <Layers className="w-3 h-3" /> FM
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap text-sm text-slate-500">
          <span>Grade {test.grade}</span>
          <span>·</span>
          <span>{test.questions.length} questions</span>
          {test.add_marks && test.total_marks && (
            <>
              <span>·</span>
              <span>{test.total_marks} marks total</span>
            </>
          )}
          {isFM && test.fm_difficulty && (
            <>
              <span>·</span>
              <span className="font-medium text-fuchsia-700">{test.fm_difficulty}</span>
            </>
          )}
          <span className="ml-auto text-sky-700 font-semibold">
            {answeredCount}/{test.questions.length} answered
          </span>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6" data-testid="test-questions">
        {test.questions.map((q, idx) => {
          const ans = answers[q.question_number] || {};
          const isTITA = q.answer_type === "TITA";
          const isFMTITA = isTITA && isFM;
          return (
            <Card
              key={idx}
              className="rounded-2xl p-5 border border-slate-200 bg-white"
              data-testid={`question-card-${q.question_number}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <p className="font-semibold text-slate-800">
                  <span className="text-slate-400 font-normal mr-1">Q{q.question_number}.</span>
                  {q.question_text}
                </p>
                {q.marks && test.add_marks && (
                  <span className="text-xs text-slate-500 flex-shrink-0 border border-slate-200 rounded px-2 py-0.5">
                    {q.marks} mark{q.marks !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {q.question_image && (
                <img
                  src={q.question_image}
                  alt="Question"
                  className="max-h-48 rounded-xl mb-4 object-contain border border-slate-100"
                />
              )}

              {q.answer_type === "MCQ" && (
                <div className="space-y-2" data-testid={`mcq-options-${q.question_number}`}>
                  {(q.options || []).map((opt, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleSelect(q.question_number, opt)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition ${
                        ans.selected_option === opt
                          ? "bg-violet-600 text-white border-violet-600 font-semibold"
                          : "bg-white text-slate-700 border-slate-200 hover:border-violet-300 hover:bg-violet-50"
                      }`}
                      data-testid={`option-btn-${q.question_number}-${optIdx}`}
                    >
                      <span className="font-bold mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {isFMTITA ? (
                // FM numeric input — single line, number type
                <div data-testid={`fm-tita-input-${q.question_number}`}>
                  <input
                    type="number"
                    value={ans.typed_answer || ""}
                    onChange={(e) => handleTyped(q.question_number, e.target.value)}
                    placeholder="Enter your answer"
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300 text-center text-lg font-bold"
                  />
                </div>
              ) : isTITA ? (
                <div data-testid={`tita-input-${q.question_number}`}>
                  <textarea
                    rows={3}
                    value={ans.typed_answer || ""}
                    onChange={(e) => handleTyped(q.question_number, e.target.value)}
                    placeholder="Type your answer here..."
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>

      {/* Submit bar */}
      <div className="sticky bottom-4 mt-8">
        <Card className="rounded-2xl p-3 sm:p-4 bg-white border border-slate-200 shadow-lg flex items-center justify-between gap-2 sm:gap-4">
          <div className="text-sm text-slate-600 min-w-0">
            <span className="font-semibold text-slate-800">{answeredCount}</span>
            {" / "}
            <span className="font-semibold text-slate-800">{test.questions.length}</span>
            <span className="hidden sm:inline"> answered</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={() => setShowAbandonConfirm(true)}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 text-sm px-3"
              data-testid="abandon-test-btn"
            >
              Abandon
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className={isFM ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white" : "bg-violet-600 hover:bg-violet-700 text-white"}
              data-testid="submit-test-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
